/**
 * Content Generation Service
 * 
 * Shared service for generating AI-powered content suggestions.
 * Used by both Header and ContentDashboard components.
 */

import {
  generateScrollytellingContent as generateScrollytellingAPI,
  generateSimpleContent as generateSimpleContentAPI,
  suggestVisualSearchTerm
} from './ContentSuggesterAPI';
import { APISearchVisuals } from './APIFunctions';

/**
 * Generate content based on type and user settings
 * 
 * @param {string} contentType - Type of content to generate
 * @param {object} userSettings - User settings including mission statement and preferences
 * @param {function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise} Promise resolving to generated content
 */
export const generateContent = async (contentType, userSettings, onProgress = null) => {
  try {
    if (onProgress) onProgress(1);

    if (contentType === 'scrollytelling') {
      const prefs = userSettings.wizardPreferences || {};

      // Respect the user's toggle choices for mission statement and institution profile
      const useMission = prefs.useMissionStatement ?? !!userSettings.missionStatement;
      const useInstitution = prefs.useInstitutionProfile ?? !!userSettings.institutionProfile;

      // Use the editable mission statement text if the user modified it in the wizard
      const missionToSend = useMission
        ? (prefs.missionStatementText || userSettings.missionStatement || '')
        : '';
      const institutionToSend = useInstitution
        ? (userSettings.institutionProfile || '')
        : '';

      const result = await generateScrollytellingAPI({
        missionStatement: missionToSend,
        institutionProfile: institutionToSend,
        specificPrompt: prefs.specificPrompt || '',
        educationalLevel: prefs.educationalLevel || 'general',
        numPapers: 10,
        numSlides: prefs.numSlides || 5,
        outputLanguage: prefs.outputLanguage || 'en'
      });

      if (onProgress) onProgress(100);
      return { variations: [result], contentType: 'scrollytelling' };
    } else {
      // Generate simple content using lightweight LLM
      const result = await generateSimpleContentAPI({
        missionStatement: userSettings.missionStatement || '',
        institutionProfile: userSettings.institutionProfile || '',
        specificPrompt: userSettings.wizardPreferences?.specificPrompt || '',
        contentType: contentType,
        educationalLevel: userSettings.wizardPreferences?.educationalLevel || 'general',
        preferences: {
          useMissionStatement: Boolean(userSettings.missionStatement),
          useInstitutionProfile: Boolean(userSettings.institutionProfile),
          useSpecificPrompt: Boolean(userSettings.wizardPreferences?.specificPrompt),
          useContentAnalytics: false // Hidden per requirements
        }
      });

      if (onProgress) onProgress(100);
      return { variations: [result], contentType: contentType };
    }
  } catch (error) {
    if (onProgress) onProgress(0);
    throw error;
  }
};

/**
 * Validate that user has at least one preference set for generation
 */
export const validateGenerationPreferences = (userSettings) => {
  const hasMission = Boolean(userSettings.missionStatement);
  const hasInstitution = Boolean(userSettings.institutionProfile);
  const hasPrompt = Boolean(userSettings.wizardPreferences?.specificPrompt);

  return hasMission || hasInstitution || hasPrompt;
};

/**
 * Generate visual search results using AI-extracted search term
 * For standalone visual AI creation flow
 * 
 * @param {object} userSettings - User settings including mission statement and prompt
 * @param {function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise} Promise resolving to {searchTerm, results, contextUsed}
 */
export const generateVisualSearchResults = async (userSettings, onProgress = null) => {
  try {
    if (onProgress) onProgress(20);
    
    // Get AI-suggested search term
    const termResult = await suggestVisualSearchTerm({
      missionStatement: userSettings.missionStatement || '',
      specificPrompt: userSettings.wizardPreferences?.specificPrompt || ''
    });
    
    if (onProgress) onProgress(50);
    
    // Search visuals using the suggested term
    const searchResults = await new Promise((resolve, reject) => {
      APISearchVisuals(
        termResult.search_term,
        20, // max results
        (results) => resolve(results),
        (error) => reject(error)
      );
    });
    
    if (onProgress) onProgress(100);
    
    return {
      searchTerm: termResult.search_term,
      results: searchResults,
      contextUsed: termResult.context_used
    };
    
  } catch (error) {
    if (onProgress) onProgress(0);
    throw error;
  }
};

/**
 * Get user-friendly error message for generation failures
 */
export const getGenerationErrorMessage = (error) => {
  if (error.message?.includes('Failed to fetch')) {
    return 'Unable to connect to content generation service. Please ensure the backend is running.';
  }
  
  if (error.message?.includes('404')) {
    return 'Content generation endpoint not found. Please check backend configuration.';
  }

  return error.message || 'Failed to generate content. Please try again.';
};
